import { useEffect, useRef, useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlinePlus, AiOutlineClose } from "react-icons/ai";

// Panel para elegir qué columnas ver (al estilo de Notion): el botón + al final de
// la cabecera abre la lista de columnas con un ojo para ocultar o mostrar cada una.
// La primera columna queda siempre visible (como el título en Notion), para que la
// tabla nunca quede vacía.
const columnLabel = (column) => (typeof column.Header === "string" ? column.Header : column.id);

const ColumnSettings = ({ columns, onToggle, onShowAll, onHideAll, lockedColumnId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState(null);
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  // Posición fija junto al botón: la tabla tiene desplazamiento horizontal y un
  // panel absoluto quedaría recortado dentro de ella.
  const openPanel = () => {
    const rect = toggleRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        top: Math.min(rect.bottom + 6, window.innerHeight - 120),
        right: Math.max(window.innerWidth - rect.right, 8),
      });
    }
    setOpen(true);
  };

  // Cerrar al hacer clic fuera del panel.
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const term = search.trim().toLowerCase();
  const matches = (column) => !term || columnLabel(column).toLowerCase().includes(term);
  const visible = columns.filter((column) => column.isVisible && matches(column));
  const hidden = columns.filter((column) => !column.isVisible && matches(column));

  const renderItem = (column) => {
    const locked = column.id === lockedColumnId;
    return (
      <li key={column.id} className="gm-column-settings-item">
        <span>{columnLabel(column)}</span>
        <button
          type="button"
          disabled={locked}
          title={locked ? "Esta columna siempre se muestra" : column.isVisible ? "Ocultar columna" : "Mostrar columna"}
          onClick={() => onToggle(column.id)}
        >
          {column.isVisible ? <AiOutlineEye size={18} /> : <AiOutlineEyeInvisible size={18} />}
        </button>
      </li>
    );
  };

  return (
    <div className="gm-column-settings" ref={panelRef}>
      <button
        type="button"
        ref={toggleRef}
        className="gm-column-settings-toggle"
        title="Elegir columnas visibles"
        onClick={(event) => { event.stopPropagation(); if (open) setOpen(false); else openPanel(); }}
      >
        <AiOutlinePlus size={16} />
      </button>
      {open && (
        <div
          className="gm-column-settings-panel"
          role="dialog"
          aria-label="Columnas visibles"
          style={position ? { top: position.top, right: position.right } : undefined}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="gm-column-settings-header">
            <strong>Columnas de la tabla</strong>
            <button type="button" title="Cerrar" onClick={() => setOpen(false)}>
              <AiOutlineClose size={16} />
            </button>
          </div>
          <input
            type="text"
            className="gm-column-settings-search"
            placeholder="Buscar una columna..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
          <div className="gm-column-settings-section">
            <span>Visibles en la tabla</span>
            <button type="button" onClick={onHideAll}>Ocultar todo</button>
          </div>
          <ul>{visible.map(renderItem)}</ul>
          <div className="gm-column-settings-section">
            <span>Ocultas en la tabla</span>
            <button type="button" onClick={onShowAll}>Mostrar todo</button>
          </div>
          <ul>{hidden.length ? hidden.map(renderItem) : <li className="gm-column-settings-empty">Ninguna</li>}</ul>
        </div>
      )}
    </div>
  );
};

export default ColumnSettings;
